/**
 * Bundled FHIR-aligned sample DOCUMENTS (DocumentReferenceView) — demo fallback.
 *
 * Used by Documents when the deployed site cannot reach the clinical API (or
 * the patient has no rows yet): the section renders these contract-exact
 * samples with a "Sample data" badge instead of an empty or error panel, so
 * the demo stays fully clickable on pages.dev.
 *
 * Shapes mirror Dex's deployed `_document_to_view` EXACTLY
 * (docs/design/provider-backend-api-2026-08-30.md §6.2): `resource_type:
 * 'DocumentReference'`, doc_type from the deployed whitelist, metadata-only
 * (the binary body is never inlined — `storage_path` is a demo-local path),
 * `content_status: 'available'`, retention class, and `encounter_id` linking
 * two of the documents to the sample encounters in ./encounters.ts.
 *
 * ids are stable UUIDs — detail lookups match them verbatim.
 */
import type { DocumentReferenceView } from '../../api/clinical'

const PATIENT_ID = '7f381bff-5fa9-4fee-92a0-f8e21a163e19'

export const SAMPLE_DOCUMENTS: DocumentReferenceView[] = [
  {
    id: '7d000001-0000-4000-8000-000000000001',
    resource_type: 'DocumentReference',
    patient_id: PATIENT_ID,
    encounter_id: '5a000001-0000-4000-8000-000000000001',
    doc_type: 'lab-report',
    title: 'HbA1c + lipid panel — Aug 2026',
    description: 'Quarterly diabetes labs drawn 2026-08-17 (Al-Nour Diagnostics). HbA1c 7.4%; LDL 2.9 mmol/L; eGFR 92.',
    filename: 'patient_labs_2026-08-17.pdf',
    mime_type: 'application/pdf',
    size_bytes: 48213,
    storage_path: '/demo/docs/patient_labs_2026-08-17.pdf',
    sha256: 'd021ba33059177d5847bdf624c919384cfd86e0eed1df3aaa169164fb78a5b76',
    uploaded_by: 'Dr. Layal Haddad',
    uploaded_at: '2026-08-18T09:45:00+00:00',
    retention_class: 'clinical',
    content_status: 'available',
    created_at: '2026-08-18T09:45:00+00:00',
    updated_at: '2026-08-18T09:45:00+00:00',
  },
  {
    id: '7d000002-0000-4000-8000-000000000002',
    resource_type: 'DocumentReference',
    patient_id: PATIENT_ID,
    encounter_id: '5a000004-0000-4000-8000-000000000004',
    doc_type: 'discharge-summary',
    title: 'Discharge summary — ER visit (2026-06-30)',
    description: 'Acute URI, supportive care; Arabic discharge instructions included.',
    filename: 'er_discharge_summary_2026-06-30.pdf',
    mime_type: 'application/pdf',
    size_bytes: 96102,
    storage_path: '/demo/docs/er_discharge_summary_2026-06-30.pdf',
    sha256: '94dd3f7b1c0a2d58fb0ad4b7f6c1e2ab45c9d0012fe39ab07c6e51d8e0c4f1229c',
    uploaded_by: 'Dr. Hana Barakat',
    uploaded_at: '2026-07-01T01:45:00+00:00',
    retention_class: 'clinical',
    content_status: 'available',
    created_at: '2026-07-01T01:45:00+00:00',
    updated_at: '2026-07-01T01:45:00+00:00',
  },
  {
    id: '7d000003-0000-4000-8000-000000000003',
    resource_type: 'DocumentReference',
    patient_id: PATIENT_ID,
    encounter_id: null,
    doc_type: 'referral-letter',
    title: 'Ophthalmology referral — diabetic retinal screen',
    description: 'Annual diabetic retinal screening referral to Al-Bassel Eye Institute; last screen 2025-04 showed no retinopathy.',
    filename: 'ophthalmology_referral_2026-05-12.pdf',
    mime_type: 'application/pdf',
    size_bytes: 35880,
    storage_path: '/demo/docs/ophthalmology_referral_2026-05-12.pdf',
    sha256: '1c64fb02ae59e1b8f1a0b2e9a6d4c35f07e8b41a9d62c0fe55b8a47e9031de44',
    uploaded_by: 'Dr. Layal Haddad',
    uploaded_at: '2026-05-12T10:45:00+00:00',
    retention_class: 'clinical',
    content_status: 'available',
    created_at: '2026-05-12T10:45:00+00:00',
    updated_at: '2026-05-12T10:45:00+00:00',
  },
  {
    id: '7d000004-0000-4000-8000-000000000004',
    resource_type: 'DocumentReference',
    patient_id: PATIENT_ID,
    encounter_id: '5a000004-0000-4000-8000-000000000004',
    doc_type: 'imaging-report',
    title: 'Chest X-ray — no acute findings',
    description: 'PA chest radiograph during ER visit 2026-06-30: lungs clear, no consolidation; cardiac silhouette normal.',
    filename: 'cxr_report_2026-06-30.pdf',
    mime_type: 'application/pdf',
    size_bytes: 51204,
    storage_path: '/demo/docs/cxr_report_2026-06-30.pdf',
    sha256: 'a9f31be20c47d91e6c5aa09b7d22e4f8c18b06d3e7f5a24c9960b1d5ee8c73b1',
    uploaded_by: 'Radiology — Al-Nour DMC',
    uploaded_at: '2026-07-01T00:50:00+00:00',
    retention_class: 'clinical',
    content_status: 'available',
    created_at: '2026-07-01T00:50:00+00:00',
    updated_at: '2026-07-01T00:50:00+00:00',
  },
  {
    id: '7d000005-0000-4000-8000-000000000005',
    resource_type: 'DocumentReference',
    patient_id: PATIENT_ID,
    encounter_id: null,
    doc_type: 'consent',
    title: 'Telehealth consent — Diabetes Program',
    description: 'Signed consent for virtual diabetes follow-up consultations (Arabic + English).',
    filename: 'telehealth_consent_2026-07.pdf',
    mime_type: 'application/pdf',
    size_bytes: 27440,
    storage_path: '/demo/docs/telehealth_consent_2026-07.pdf',
    sha256: '50b3ee8d2a41c06f99e17a3b5d80c21f4a96b7d308c1e59f0d47b2a6c3e19055',
    uploaded_by: 'Rana Khalil, RN',
    uploaded_at: '2026-07-22T15:30:00+00:00',
    retention_class: 'legal',
    content_status: 'available',
    created_at: '2026-07-22T15:30:00+00:00',
    updated_at: '2026-07-22T15:30:00+00:00',
  },
]
